import * as React from "react";
import { Button } from "../ui/button";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { useEffect, useMemo } from "react";
import {
  getAxiosErorrMessage,
  logAxiosError,
  useActiveProject,
  useCanvasId,
  useUserId,
} from "@/utils/utils";
import axios from "axios";
import { cn } from "@/lib/utils";
import {
  ExternalLink,
  ExternalLinkIcon,
  Loader2,
  XCircle,
  CheckCircle,
  MinusCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Separator } from "../ui/separator";
import { useStartServer } from "@/utils/devServerUtils";
import { useUser } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";
// Utility functions for Supabase project status
const disabledStatuses = [
  "INACTIVE",
  "ACTIVE_UNHEALTHY",
  "GOING_DOWN",
  "INIT_FAILED",
  "REMOVED",
  "PAUSING",
  "RESTORE_FAILED",
  "PAUSE_FAILED",
];

const formatStatusLabel = (status: string): string => {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

interface Props {
  forProjectCreate?: boolean;
  onConnectionStatusChange?: (connected: boolean) => void;
}

const ProjectSelector = ({
  selectProject,
  setSelectedHasExistingProjectDetails,
  fetchConnectedOrg,
}: {
  selectProject: (project: any) => void;
  setSelectedHasExistingProjectDetails: (
    hasExistingProjectDetails: boolean
  ) => void;
  fetchConnectedOrg: () => Promise<void>;
}) => {
  const connectedProjects = useSupabaseConnectStore(
    (state) => state.connectedProjects
  );
  const connectedOrgName = useSupabaseConnectStore(
    (state) => state.connectedOrgName
  );
  const [loadingProjectDetails, setLoadingProjectDetails] =
    React.useState(false);
  const [refreshingProjects, setRefreshingProjects] = React.useState(false);
  const canvasId = useCanvasId();
  const { session } = useSession();

  const refreshProjects = async () => {
    if (refreshingProjects) return;

    setRefreshingProjects(true);
    try {
      await fetchConnectedOrg();
    } catch (e) {
      console.error("Failed to refresh projects:", e);
      toast.error("Failed to refresh projects");
    } finally {
      setRefreshingProjects(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 bg-background-secondary p-1 rounded-sm">
      <div className="text-xs text-text-primary p-1 flex justify-between items-center">
        <span>Project List (Org {connectedOrgName}):</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0.5"
          onClick={refreshProjects}
          disabled={refreshingProjects}
        >
          {refreshingProjects ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        {connectedProjects.length === 0 && (
          <div className="p-1.5 text-xs text-red-500">
            Error: No projects found. Please create a project or select a
            different organization.
          </div>
        )}
        {connectedProjects.map((project) => {
          const statusLabel = formatStatusLabel(project.status);
          const enabled = !disabledStatuses.includes(project.status);

          return (
            <div
              className={cn(
                "hover:bg-background-tertiary text-text-primary text-xs cursor-pointer p-1.5 rounded-sm px-2 flex flex-row gap-2 items-center",
                !enabled &&
                  "opacity-50 cursor-default hover:bg-background-secondary"
              )}
              key={project.id}
              onClick={async () => {
                if (!enabled) return;

                setLoadingProjectDetails(project.id);
                if (canvasId) {
                  selectProject(project);
                  setSelectedHasExistingProjectDetails(false);
                  setLoadingProjectDetails(false);
                  return;
                }

                if (!session) return;
                const supabaseJWT = await getUnexpiredToken(session);

                try {
                  const res = await axios.post(
                    `${
                      import.meta.env.VITE_BACKEND_BASE_URL
                    }/checkExistingDB/supabase`,
                    {
                      supabaseProjectId: project.id,
                      supabaseJWT,
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${supabaseJWT}`,
                      },
                    }
                  );

                  if (res.data.success) {
                    setSelectedHasExistingProjectDetails(
                      res.data.existingProjectDetails
                    );
                  }
                  selectProject(project);
                } catch (e) {
                  console.error(e);
                  toast.error(
                    "Error checking existing project details: " +
                      getAxiosErorrMessage(e)
                  );
                } finally {
                  setLoadingProjectDetails(false);
                }
              }}
            >
              <span>
                {project.name} ({statusLabel}){" "}
              </span>
              {loadingProjectDetails === project.id && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SupabaseConnect: React.FunctionComponent<Props> = React.memo((props) => {
  const canvasId = useCanvasId();
  const project = useActiveProject();
  const { session } = useSession();

  const [
    selectedHasExistingProjectDetails,
    setSelectedHasExistingProjectDetails,
  ] = React.useState(false);
  const { user } = useUser();

  const connectedOrgId = useSupabaseConnectStore(
    (state) => state.connectedOrgId
  );
  const connectedProjects = useSupabaseConnectStore(
    (state) => state.connectedProjects
  );
  const setConnectedOrg = useSupabaseConnectStore(
    (state) => state.setConnectedOrg
  );
  const loadingConnectionState = useSupabaseConnectStore(
    (state) => state.loadingConnectionState
  );
  const setLoadingConnectionState = useSupabaseConnectStore(
    (state) => state.setLoadingConnectionState
  );
  const loadedConnectedState = useSupabaseConnectStore(
    (state) => state.loadedConnectedState
  );
  const setLoadedConnectedState = useSupabaseConnectStore(
    (state) => state.setLoadedConnectedState
  );
  const connectedProjectId = useSupabaseConnectStore(
    (state) => state.connectedProjectId
  );
  const setConnectedProjectId = useSupabaseConnectStore(
    (state) => state.setConnectedProjectId
  );

  const [selectedProject, setSelectedProject] = React.useState(null);
  const [loadingConnectProject, setLoadingConnectProject] =
    React.useState(false);
  const [waitingForOrgToConnect, setWaitingForOrgToConnect] =
    React.useState(false);

  const connectedProject = useMemo(() => {
    return connectedProjects.find(
      (_project) => _project.id === connectedProjectId
    );
  }, [connectedProjectId, connectedProjects]);

  const startServer = useStartServer();

  const userId = useUserId();

  // Notify parent component about connection status changes
  useEffect(() => {
    if (props.onConnectionStatusChange) {
      props.onConnectionStatusChange(!!connectedProjectId);
    }
  }, [connectedProjectId, props.onConnectionStatusChange]);

  const fetchConnectedOrg = async () => {
    setLoadingConnectionState(true);

    let url = `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/projects/supabase-connect/${project?.id}/connection`;
    if (props.forProjectCreate) {
      url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/projects/supabase-connect/connection/ftue`;
    }

    if (!session) return;
    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      });

      const {
        orgs,
        projects,
        connected,
        connectedProjectId: _connectedProjectId,
      } = res.data;
      const org = orgs?.[0];

      if (connected && org) {
        setConnectedOrg(org.id, org.name, projects || []);

        // For the FTUE, we only connect the project after the project gets created
        if (!props.forProjectCreate) {
          setConnectedProjectId(_connectedProjectId);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingConnectionState(false);
      setLoadedConnectedState(true);
    }
  };

  useEffect(() => {
    if (
      !session ||
      !canvasId ||
      !project ||
      loadingConnectionState ||
      loadedConnectedState
    )
      return;

    fetchConnectedOrg();
  }, [Boolean(session), canvasId, project]);

  const pollConnectedOrg = (windowToClose: Window | null) => {
    // Poll every second if the org has been connected (max 1 min)
    const startTime = new Date();
    const interval = setInterval(async () => {
      if (new Date().getTime() - startTime.getTime() > 60000) {
        clearInterval(interval);
        setWaitingForOrgToConnect(false);
        toast.error("Connection timeout. Please try again.");
        return;
      }

      let url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/projects/supabase-connect/${project?.id}/new-org-connected`;
      if (props.forProjectCreate) {
        url = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/supabase-connect/new-org-connected/ftue`;
      }

      let res;
      try {
        if (!session) return;
        const supabaseJWT = await getUnexpiredToken(session);
        res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
          params: {
            afterTs: startTime.toISOString(),
          },
        });
      } catch (e) {
        console.error("Polling error:", e);
        return;
      }

      if (res.data.newOrgConnected) {
        windowToClose?.close?.();
        clearInterval(interval);
        fetchConnectedOrg();
        setWaitingForOrgToConnect(false);
      }
    }, 1000);

    return interval;
  };

  const connectProject = async (projectId: string) => {
    if (loadingConnectProject) return;

    if (props.forProjectCreate) {
      // Just set the connected project id and return
      setConnectedProjectId(projectId);
      return;
    }

    setLoadingConnectProject(true);

    try {
      if (!session) return;
      const supabaseJWT = await getUnexpiredToken(session);

      // For existing projects, make the API call
      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/supabase-connect/${
          project.id
        }/connect-project`,
        {
          supabaseProjectId: projectId,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      if (res.data.success) {
        setConnectedProjectId(projectId);
        toast.success("Supabase project connected successfully");

        if (!props.forProjectCreate) {
          // Restart to set the new env variables
          startServer({
            forceRestart: true,
          });
        }
      }
    } catch (e) {
      logAxiosError("Error connecting supabase project", e);
      toast.error(
        "Error connecting Supabase project: " + getAxiosErorrMessage(e)
      );
    } finally {
      setLoadingConnectProject(false);
    }
  };

  const disconnectProject = async () => {
    if (loadingConnectProject) return;

    if (props.forProjectCreate) {
      // Just set the connected project id to null and return
      // @ts-expect-error TS2345
      setConnectedProjectId(null);
      return;
    }

    setLoadingConnectProject(true);

    if (!session) return;
    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/supabase-connect/${
          project.id
        }/disconnect-project`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      const { success } = res.data;
      if (success) {
        // @ts-expect-error TS2345
        setConnectedProjectId(null);
        setSelectedProject(null);

        // Restart to clear the env variables
        if (!props.forProjectCreate) {
          startServer({
            forceRestart: true,
          });
        }
      }
    } catch (e) {
      logAxiosError("Error disconnecting Supabase project", e);
      toast.error(
        "Error disconnecting Supabase project: " + getAxiosErorrMessage(e)
      );
    } finally {
      setLoadingConnectProject(false);
    }
  };

  const getDescription = () => {
    if (connectedProjectId) {
      return "Supabase has been enabled.";
    }

    if (selectedProject) {
      return "You are about to connect your project to Supabase. This will give Tempo context on your Supabase project to persist data, add authentication, write backend functions, and more.";
    }

    if (connectedOrgId) {
      return "Select a Supabase project to connect to Tempo";
    }

    return "Connect Supabase to your project to build auth, storage, database, and backend functionality.";
  };

  const SupabaseStatusTag = () => {
    let label = "Not Connected";
    let bgColor = "bg-red-500/20";
    let textColor = "text-red-500";
    let icon = <XCircle className="h-2.5 w-2.5" />;

    if (connectedOrgId && !connectedProjectId) {
      label = "Select a Project";
      bgColor = "bg-yellow-500/20";
      textColor = "text-yellow-500";
      icon = <MinusCircle className="h-2.5 w-2.5" />;
    } else if (loadingConnectionState || waitingForOrgToConnect) {
      label = "Loading...";
      bgColor = "bg-yellow-500/20";
      textColor = "text-yellow-500";
      icon = <MinusCircle className="h-2.5 w-2.5" />;
    } else if (connectedProject) {
      label = "Connected";
      bgColor = "bg-green-500/20";
      textColor = "text-green-500";
      icon = <CheckCircle className="h-2.5 w-2.5" />;
    } else {
      label = "Not Connected";
      bgColor = "bg-red-500/20";
      textColor = "text-red-500";
      icon = <XCircle className="h-2.5 w-2.5" />;
    }

    return (
      <div
        className={cn(
          "rounded-full h-2 text-primary-n-500 p-2 py-2.5 flex gap-1.5 items-center text-3xs font-medium mb-0.5 ml-auto",
          bgColor,
          textColor
        )}
      >
        <img src="/images/supabase-logo-icon.svg" className="w-2 h-2" />
        <span>{label}</span>
        {icon}
      </div>
    );
  };

  const getMainContent = () => {
    if (loadingConnectionState) {
      return (
        <div
          className={cn(
            "flex flex-row gap-2 text-text-secondary items-center mt-2"
          )}
        >
          <div className={cn("animate-spin")}>
            <Loader2 className="h-3 w-3" />
          </div>
        </div>
      );
    }

    if (connectedProject) {
      const links = [
        {
          label: "Dashboard",
          url: `https://supabase.com/dashboard/project/${connectedProject.id}`,
        },
        {
          label: "View Tables",
          url: `https://supabase.com/dashboard/project/${connectedProject.id}/editor`,
        },
        {
          label: "Manage Users",
          url: `https://supabase.com/dashboard/project/${connectedProject.id}/auth/users`,
        },
        {
          label: "SQL Editor",
          url: `https://supabase.com/dashboard/project/${connectedProject.id}/sql/new`,
        },
        {
          label: "Edge Functions",
          url: `https://supabase.com/dashboard/project/${connectedProject.id}/functions`,
        },
      ];

      return (
        <>
          <div className="flex flex-col">
            {links.map((link) => (
              <div
                className="flex flex-row justify-between items-center hover:bg-background-secondary rounded-sm cursor-pointer p-2"
                key={link.label}
                onClick={() => window.open(link.url, "_blank")}
              >
                <div className="text-xs">{link.label}</div>
                <ExternalLinkIcon className="w-3 h-3" />
              </div>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col bg-background-secondary rounded-sm p-2 gap-4">
            <div className="text-xs text-text-primary flex flex-row justify-between items-center">
              <div>{connectedProject.name}</div>
              <Button
                className="h-7 border-none bg-background-tertiary hover:bg-secondary-n-800 gap-2 items-center"
                size="sm"
                variant="secondary"
                onClick={disconnectProject}
              >
                <div
                  className={cn(
                    "animate-spin",
                    !loadingConnectProject && "hidden"
                  )}
                >
                  <Loader2 className="h-3 w-3" />
                </div>
                Disconnect
              </Button>
            </div>
          </div>
        </>
      );
    }

    if (selectedProject) {
      return (
        <>
          <div className="flex flex-col bg-background-secondary rounded-sm pt-3 pb-2 px-2 gap-4">
            <div className="text-xs text-text-primary">
              {/*
               // @ts-expect-error TS2339 */}
              {selectedProject.name}
            </div>
            {selectedHasExistingProjectDetails && (
              <div className="text-xs text-text-secondary">
                <p className="text-red-500">
                  Error: This project already has database tables or edge
                  functions setup. You must choose an empty project to connect.
                </p>
              </div>
            )}
            <div className="flex flex-row justify-betwen gap-2">
              <Button
                className="grow bg-background-secondary hover:bg-background-tertiary text-text-secondary gap-1"
                size="xs"
                variant="outline"
                onClick={() =>
                  window.open(
                    // @ts-expect-error TS2339
                    `https://supabase.com/dashboard/project/${selectedProject.id}`,
                    "_blank"
                  )
                }
              >
                Dashboard
                <ExternalLink className="w-3 h-3" />
              </Button>
              <Button
                className="grow bg-background-secondary hover:bg-background-tertiary text-text-secondary gap-1"
                size="xs"
                variant="outline"
                onClick={() =>
                  window.open(
                    // @ts-expect-error TS2339
                    `https://supabase.com/dashboard/project/${selectedProject.id}/editor`,
                    "_blank"
                  )
                }
              >
                View Tables
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="text-xs text-text-secondary">
            We won't make any changes at this step, and you can always
            disconnect
          </div>
          <div className="flex flex-row gap-2">
            <Button
              variant="secondary"
              disabled={loadingConnectProject}
              className="grow h-7 hover:bg-background-tertiary border-none basis-0"
              size="sm"
              onClick={() => setSelectedProject(null)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                loadingConnectProject || selectedHasExistingProjectDetails
              }
              className="grow h-7 gap-2 items-center basis-0"
              size="sm"
              // @ts-expect-error TS2339
              onClick={() => connectProject(selectedProject.id)}
            >
              <div
                className={cn(
                  "animate-spin",
                  !loadingConnectProject && "hidden"
                )}
              >
                <Loader2 className="h-3 w-3" />
              </div>
              Connect
            </Button>
          </div>
        </>
      );
    }

    if (connectedOrgId) {
      return (
        <>
          <ProjectSelector
            selectProject={setSelectedProject}
            setSelectedHasExistingProjectDetails={
              setSelectedHasExistingProjectDetails
            }
            fetchConnectedOrg={fetchConnectedOrg}
          />
          <Button
            variant="secondary"
            className="text-xs h-8 mt-1 border-none hover:bg-background-tertiary items-center gap-2"
            onClick={() => {
              if (!project?.id && !props.forProjectCreate) {
                toast.error("No active project found");
                return;
              }

              setWaitingForOrgToConnect(true);
              let proxy;
              if (props.forProjectCreate) {
                proxy = window.open(
                  `${
                    import.meta.env.VITE_BACKEND_SUPABASE_URL
                  }/functions/v1/connect-supabase/${userId}/connect-ftue`,
                  "supabaseConnect",
                  "width=800,height=800"
                );
              } else {
                proxy = window.open(
                  `${
                    import.meta.env.VITE_BACKEND_SUPABASE_URL
                  }/functions/v1/connect-supabase/${project?.id}/connect`,
                  "supabaseConnect",
                  "width=800,height=800"
                );
              }

              pollConnectedOrg(proxy);
            }}
          >
            <div
              className={cn(
                "animate-spin",
                !waitingForOrgToConnect && "hidden"
              )}
            >
              <Loader2 className="h-3 w-3" />
            </div>
            <span>Connect a different organization</span>
          </Button>
        </>
      );
    }

    return (
      <Button
        variant="secondary"
        className={cn("gap-1 text-xs h-8 mt-2 border-none")}
        onClick={() => {
          if (!project?.id && !props.forProjectCreate) {
            toast.error("No active project found");
            return;
          }

          setWaitingForOrgToConnect(true);
          let url;
          if (props.forProjectCreate) {
            url = `${
              import.meta.env.VITE_BACKEND_SUPABASE_URL
            }/functions/v1/connect-supabase/${userId}/connect-ftue`;
          } else {
            url = `${
              import.meta.env.VITE_BACKEND_SUPABASE_URL
            }/functions/v1/connect-supabase/${project?.id}/connect`;
          }

          const proxy = window.open(
            url,
            "supabaseConnect",
            "width=800,height=800"
          );
          if (!proxy) {
            toast.error("Failed to open window to log in, please try again");
            return;
          }

          pollConnectedOrg(proxy);
        }}
      >
        <div
          className={cn("animate-spin", !waitingForOrgToConnect && "hidden")}
        >
          <Loader2 className="h-3 w-3" />
        </div>
        <img
          src="/images/supabase-logo-icon.svg"
          className={cn("w-4 h-4", waitingForOrgToConnect && "hidden")}
        />
        <span>Connect Supabase</span>
      </Button>
    );
  };

  return (
    <div className="flex flex-col w-full p-2 gap-2">
      <div className="flex flex-row gap-2 items-center">
        {selectedProject && !connectedProjectId ? (
          <>
            <img src="/images/supabase-logo-icon.svg" className="w-4 h-4" />
            <div className="text-text-primary font-medium text-xs">
              Connect this project to Tempo
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-text-primary font-medium">
              Supabase
            </div>
            <div className="bg-[#503ABF4D] rounded-xs text-primary-n-600 text-xs py-0.5 px-1.5">
              BETA
            </div>
            <SupabaseStatusTag />
          </>
        )}
      </div>
      <div className="text-xs text-text-secondary">{getDescription()}</div>
      {getMainContent()}
    </div>
  );
});

export default SupabaseConnect;
